import type { FC, ReactNode } from "react";
import { cx } from "@/utils/cx";

export const DisplayFieldState = {
	Enabled: "enabled",
	Hovered: "hovered",
	Active: "active",
	Error: "error",
	Disabled: "disabled",
} as const;

export type DisplayFieldState =
	(typeof DisplayFieldState)[keyof typeof DisplayFieldState];

export const DisplayFieldContent = {
	Empty: "empty",
	Populated: "populated",
} as const;

export type DisplayFieldContent =
	(typeof DisplayFieldContent)[keyof typeof DisplayFieldContent];

export const DisplayFieldFeedback = {
	Idle: "idle",
	Saving: "saving",
	Saved: "saved",
	/** Saved visuals at 0 opacity — exit/fade before returning to Idle. */
	End: "end",
} as const;

export type DisplayFieldFeedback =
	(typeof DisplayFieldFeedback)[keyof typeof DisplayFieldFeedback];

/** How long Saved stays fully visible before End fade. */
export const FEEDBACK_SAVED_DISPLAY_MS = 1600;

/** Opacity transition Saved → End, then Idle. Must match FeedbackIndicator fade. */
export const FEEDBACK_SAVED_FADE_MS = 300;

export type DisplayFieldIcon = FC<{ className?: string }>;

export interface DisplayFieldBaseProps {
	/** Field label (floats above the value when populated / active). */
	label: string;
	/** Visual interaction state. @default "enabled" */
	state?: DisplayFieldState;
	/** Empty vs populated content. Derived from `value` when omitted. */
	content?: DisplayFieldContent;
	/** Optional leading icon. */
	leadingIcon?: DisplayFieldIcon;
	/** Show leading icon when provided. @default true when icon is set */
	showLeadingIcon?: boolean;
	/** Hint / error helper below the field. */
	hintText?: string;
	/** Show hint text. @default false (true when state is error and hintText is set) */
	showHintText?: boolean;
	/**
	 * Auto-save FeedbackIndicator (Figma): idle | saving | saved | end.
	 * Hides the edit affordance while saving / saved / end.
	 * Sequence: Saving → Saved → End (fade) → Idle.
	 */
	feedback?: DisplayFieldFeedback;
	/**
	 * Show the trailing edit affordance.
	 * @default true when not disabled
	 */
	showEdit?: boolean;
	/** Called when the edit affordance is activated. */
	onEdit?: () => void;
	/** Accessible name for the edit control. */
	editAriaLabel?: string;
	/**
	 * Draft value while `state` is Active (inline edit).
	 * When omitted, DisplayInput / DisplayTextarea fall back to stringified `value`.
	 */
	editValue?: string;
	/**
	 * Called as the user types while Active.
	 * Parent should update local draft only — persist on `onEditCommit` (leave field).
	 */
	onEditChange?: (value: string) => void;
	/**
	 * Called when leaving edit mode (blur / Enter).
	 * Parent should persist the draft here (and end editing), not mid-keystroke.
	 */
	onEditCommit?: () => void;
	/** Called on Escape to discard the draft and cancel pending saves. */
	onEditCancel?: () => void;
	className?: string;
	"data-testid"?: string;
}

/**
 * Resolves empty vs populated from an explicit content prop or value presence.
 */
export function resolveDisplayContent(
	content: DisplayFieldContent | undefined,
	value: ReactNode,
): DisplayFieldContent {
	if (content) return content;
	if (value == null || value === "") return DisplayFieldContent.Empty;
	return DisplayFieldContent.Populated;
}

/**
 * Whether the trailing edit affordance should render.
 */
export function shouldShowEditAffordance(options: {
	state: DisplayFieldState;
	feedback: DisplayFieldFeedback;
	isEditable: boolean;
}): boolean {
	const { state, feedback, isEditable } = options;
	if (!isEditable) return false;
	if (state === DisplayFieldState.Active) return false;
	if (state === DisplayFieldState.Disabled) return false;
	if (feedback === DisplayFieldFeedback.Saving) return false;
	if (feedback === DisplayFieldFeedback.Saved) return false;
	if (feedback === DisplayFieldFeedback.End) return false;
	return true;
}

/**
 * Trailing edit / copy / chevron icon tone.
 * Rest: quaternary. Any field hover (group) or Hovered/Active state: secondary.
 */
export function displayFieldTrailingIconToneClass(options: {
	isDisabled?: boolean;
	isEmphasized?: boolean;
}): string {
	const { isDisabled = false, isEmphasized = false } = options;
	if (isDisabled) return "text-fg-quaternary";
	return cx(
		"text-fg-quaternary group-hover:text-fg-secondary",
		isEmphasized && "text-fg-secondary",
	);
}
